import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { CONCEPT_GROUP_NAME_MAX_LENGTH } from './concept-group/hooks/useCreateConceptGroupModal';
import {
  classifyRenameFailure,
  useRenameConceptGroupMutation,
} from '../lib/api/useRenameConceptGroupMutation';

interface RenameConceptGroupDialogProps {
  projectId: string;
  conceptGroupId: string;
  name: string;
  onClose: () => void;
}

/**
 * Rename dialog opened from a Draft concept-group row on the artifacts page. Renaming
 * in place is what keeps a researcher's position in the list while they work down a
 * bulk upload, so this never navigates.
 *
 * Uniqueness is judged by the server on submit — a refusal renders on the field with
 * the typed text intact rather than closing the dialog.
 *
 * TODO: cleanup with concept_group_rename ff
 */
export const RenameConceptGroupDialog = ({
  projectId,
  conceptGroupId,
  name,
  onClose,
}: RenameConceptGroupDialogProps) => {
  const { formatMessage } = useIntl();
  const rename = useRenameConceptGroupMutation(projectId);
  const [value, setValue] = useState(name);
  const [errorId, setErrorId] = useState<string | null>(null);

  const trimmed = value.trim();
  const isPending = rename.isPending;

  const handleSubmit = async () => {
    if (!trimmed || isPending) return;
    if (trimmed === name) {
      onClose();
      return;
    }
    try {
      await rename.mutateAsync({ conceptGroupId, conceptGroupName: trimmed });
      onClose();
    } catch (error) {
      const failure = classifyRenameFailure(error);
      setErrorId(
        failure === 'duplicate'
          ? 'conceptGroups.renameConceptGroupModal.duplicateName'
          : failure === 'conflict'
          ? 'conceptGroups.renameConceptGroupModal.conflict'
          : 'conceptGroups.renameConceptGroupModal.error'
      );
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid={`concept-group-row-${conceptGroupId}-rename-dialog`}
      onClick={(event) => event.stopPropagation()}
    >
      <DialogTitle>
        {formatMessage({ id: 'conceptGroups.renameConceptGroupModal.title' })}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={formatMessage({ id: 'conceptGroups.renameConceptGroupModal.nameLabel' })}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setErrorId(null);
          }}
          onFocus={(event) => event.target.select()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleSubmit();
          }}
          disabled={isPending}
          error={Boolean(errorId)}
          helperText={errorId ? formatMessage({ id: errorId }) : undefined}
          inputProps={{
            maxLength: CONCEPT_GROUP_NAME_MAX_LENGTH,
            'data-testid': 'concept-group-rename-input',
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {formatMessage({ id: 'conceptGroups.renameConceptGroupModal.cancelButton' })}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          // Nothing to save while the name is empty or still the committed one.
          disabled={isPending || trimmed.length === 0 || trimmed === name.trim()}
          data-testid={`concept-group-row-${conceptGroupId}-rename-submit`}
        >
          {formatMessage({
            id: isPending
              ? 'conceptGroups.renameConceptGroupModal.saving'
              : 'conceptGroups.renameConceptGroupModal.saveButton',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
